import {
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { DocumentItemPartial } from '@domain';
import { OwnFieldRevisionsService } from 'src/app/domain/controllers/document/own-field-revisions.service';
import { RaEditorComponent } from 'src/app/shared/components/ra-editor/ra-editor.component';
import { RaViewerTextComponent } from 'src/app/shared/components/ra-viewer-text/ra-viewer-text.component';
import { EditDataService } from '../../../../services/_edit-data.service';
import { EditItemsService } from '../../../../services/edit-items.service';
import { TextEditingService, TextTarget } from '../../../../services/text-editing.service';

/**
 * Texto do campo editável DIRETO na folha (título, subtítulo, texto, descrição de mídia e
 * texto do destaque). Substituiu o composer de rodapé em 2026-09-25: o `ra-editor` em modo
 * inline é o próprio texto do card, e a barra do topo do editor formata o que está em foco.
 *
 * Salva sozinho: 700ms depois da última tecla e ao sair do editor, sempre com a base
 * (texto + revisão do começo da edição) do compare-and-set do backend. Um save por vez:
 * o seguinte só parte quando o anterior responde, senão os dois mandariam a mesma revisão
 * e o segundo seria recusado como edição concorrente. Falhou: o texto fica no editor e a
 * base não muda; a próxima tecla ou o blur tentam de novo (era o `restoreValue` do composer).
 *
 * Enquanto o editor está em foco ele não recebe valor de fora: o eco do realtime não pode
 * atropelar o que está sendo digitado. Fora do foco, o texto é o do campo (IA, restauração
 * do original e outras sessões chegam normalmente).
 */
@Component({
    selector: 'inline-text-editor',
    templateUrl: 'inline-text-editor.component.html',
    styleUrl: 'inline-text-editor.component.scss',
    imports: [RaEditorComponent, RaViewerTextComponent],
    host: {
        '[style.font-family]': 'fontFamily()',
        '[style.--item-size]': 'sizeRem()',
        '[style.--item-color]': 'color()',
        '[class.editing]': 'editing()',
        '(pointerdown)': 'onPointerDown()',
    },
})
export class InlineTextEditorComponent {
    public field = input.required<DocumentItemPartial>();
    /** `value` nos textuais; `description` na mídia e no destaque. */
    public target = input<TextTarget>('value');
    public placeholder = input<string>('');
    /** Lixeira e favoritos: só leitura, com o viewer do PDF. */
    public readonly = input(false);
    /** Seção travada, documento bloqueado ou carregando: o editor não aceita foco novo. */
    public blocked = input(false);
    /** Destaque e tabela: a cor é da família da caixa, não do texto. */
    public allowColor = input(true);

    private static readonly SAVE_DEBOUNCE_MS = 700;

    public editDataService = inject(EditDataService);
    private _editItems = inject(EditItemsService);
    private _textEditing = inject(TextEditingService);
    private _ownRevs = inject(OwnFieldRevisionsService);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);
    private _editor = viewChild(RaEditorComponent);

    /**
     * Cursor "gordo": o nativo (1px, piscando) some (caret-color transparente) e este
     * traço de 2px na cor primária fica no lugar dele. É o destaque da edição desde que o
     * fundo azul do card saiu. Medido a cada frame enquanto o editor está em foco, a partir
     * da seleção do documento; some quando há um trecho selecionado (a seleção nativa
     * já mostra onde se está).
     */
    public caretVisible = signal(false);
    private _caretEl = viewChild<ElementRef<HTMLElement>>('caretEl');
    /** Última posição escrita, para não tocar no estilo quando nada mudou. */
    private _caretAt = { left: NaN, top: NaN, height: NaN };
    private _caretFrame = 0;

    public fontFamily = computed(() => this.editDataService.documentFontFamily());
    public editing = computed(() => this._textEditing.editingFieldId() === this.field()._id);
    public sizeRem = computed(() => this._textEditing.sizeRem(this.field()));
    public color = computed(() =>
        this.allowColor() ? this._textEditing.color(this.field()) : null
    );

    /** O que o editor mostrava quando ganhou o foco: fica congelado até ele sair. */
    private _frozen = signal('');

    /**
     * Valor entregue ao `ra-editor`. Em foco, o congelado; fora dele, o texto do campo, lido
     * na hora (o save otimista muda o item por dentro, sem trocar o objeto do signal).
     */
    public editorValue = computed(() => (this.editing() ? this._frozen() : this.readText()));

    /**
     * Base do compare-and-set DESTE campo: texto e revisão do começo da edição, avançados a
     * cada save que dá certo. Fica no componente, não no serviço: um save que responde
     * depois de o foco ir para outro campo (ou o que ainda sai no blur) precisa da base
     * dele, não da do campo que está em foco agora.
     */
    private _baseText: string | null = null;
    private _baseRev: number | null = null;

    /** Último pedido de foco já atendido (ver o efeito do construtor). */
    private _handledFocusVersion = -1;

    private _pending: string | null = null;
    private _lastSaved = '';
    private _inFlight = false;
    private _flushAfter = false;
    private _timer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        // Pedido de foco (campo novo, "Editar" na prévia, criar acima/abaixo): o card foca
        // o editor assim que se vê pedido, mesmo tendo nascido depois do pedido.
        //
        // Cada pedido é atendido UMA vez (pela versão): o objeto do campo é trocado a cada eco
        // do servidor e a cada mudança de config, e o pedido antigo, ainda no serviço, voltava
        // a focar o editor e jogava o cursor para o fim no meio da digitação.
        effect(() => {
            const request = this._textEditing.focusRequest();
            if (!request || request.version === this._handledFocusVersion) return;
            // Lidos FORA do untracked: bloqueado (carregando) ou ainda sem editor, o pedido
            // espera, e o efeito roda de novo quando isso mudar.
            const blocked = this.blocked();
            const editor = this._editor();
            const fieldId = untracked(() => this.field()._id);
            if (request.fieldId !== fieldId || blocked || !editor) return;

            untracked(() => {
                if (this.readonly()) return;
                this._handledFocusVersion = request.version;
                editor.focus();
            });
        });

        inject(DestroyRef).onDestroy(() => {
            this._cancelTimer();
            this._stopCaret();
            if (this.editing()) {
                this._flush();
                this._textEditing.endEditing(this.field()._id);
            }
            this._textEditing.unregisterEditor(this.field()._id);
        });
    }

    /** Texto atual do campo (Delta em string), no alvo deste editor. */
    public readText(): string {
        const field = this.field();
        const raw = this.target() === 'value' ? field.value : field.description;
        if (raw == null || raw === '') return '';
        return typeof raw === 'string' ? raw : (JSON.stringify(raw) ?? '');
    }

    public onPointerDown(): void {
        if (!this.readonly()) this._textEditing.setActive(this.field()._id, this._editor() ?? null);
    }

    public onFocus(): void {
        const field = this.field();
        const current = this.readText();

        this._frozen.set(current);
        // Com save em voo o texto do campo já é o otimista: a base continua a do save,
        // que a atualiza quando responder.
        if (!this._inFlight) {
            this._lastSaved = current;
            this._pending = null;
            this._baseText = current;
            this._baseRev = field.rev ?? null;
        }
        this._textEditing.beginEditing(field._id, this._editor() ?? null);
        this._startCaret();
    }

    public onFormatChange(event: { format: Record<string, unknown>; hasSelection: boolean }): void {
        if (!this.editing()) return;
        this._textEditing.selectionFormat.set(event.format);
        this._textEditing.hasSelection.set(event.hasSelection);
    }

    /** Só o Quill chama isto por digitação do usuário (conteúdo aplicado de fora é `silent`). */
    public onChange(value: string): void {
        if (!this.editing()) return;

        this._pending = value;
        this._scheduleFlush();
    }

    public onLeave(value: string): void {
        const field = this.field();
        this._stopCaret();
        if (this.editing()) {
            if (this._pending !== null) {
                this._pending = value;
                // Já no item, antes de sair: fora de foco o editor passa a mostrar o texto do
                // campo, e com um save ainda em voo o `_flush` só aplica depois. Sem isto as
                // últimas letras sumiam e voltavam um segundo depois.
                this._applyLocally(value);
            }
            this._flush();
        }
        this._textEditing.endEditing(field._id);
    }

    private _scheduleFlush(): void {
        this._cancelTimer();
        this._timer = setTimeout(() => this._flush(), InlineTextEditorComponent.SAVE_DEBOUNCE_MS);
    }

    private _cancelTimer(): void {
        if (this._timer) clearTimeout(this._timer);
        this._timer = null;
    }

    private _flush(): void {
        this._cancelTimer();
        if (this._pending === null) return;
        if (this._inFlight) {
            this._flushAfter = true;
            return;
        }

        const value = this._pending;
        if (value === this._lastSaved) {
            this._pending = null;
            return;
        }

        const field = this.field();
        const baseText = this._baseText ?? undefined;
        // Tamanho, cor, tipo ou IA mexeram no campo durante a edição: são escritas nossas,
        // e a base anda por cima delas. Só a de outra sessão vira conflito.
        const baseRev = this._ownRevs.fastForward(field._id, this._baseRev);

        this._inFlight = true;
        this._flushAfter = false;

        // Otimista: o card e as ações de IA do rail já leem o texto novo.
        this._applyLocally(value);

        this._editItems.updateText(
            field._id,
            value,
            () => {
                // Falhou: fica pendente com a MESMA base, para a próxima tentativa continuar
                // sendo verificada contra o começo da edição (não atropela outra sessão).
                this._inFlight = false;
                this._pending = value;
            },
            baseText,
            {
                baseRev,
                onSuccess: rev => {
                    this._inFlight = false;
                    this._lastSaved = value;
                    if (this._pending === value) this._pending = null;

                    // A revisão da RESPOSTA, não a do item: um eco atrasado do realtime
                    // pode ter devolvido ao item a revisão de um save anterior.
                    this._baseText = value;
                    if (rev != null) this._baseRev = rev;

                    if (this._flushAfter) this._flush();
                },
            }
        );
    }

    private _startCaret(): void {
        if (this._caretFrame) return;
        const tick = (): void => {
            this._measureCaret();
            this._caretFrame = requestAnimationFrame(tick);
        };
        this._caretFrame = requestAnimationFrame(tick);
    }

    private _stopCaret(): void {
        if (this._caretFrame) cancelAnimationFrame(this._caretFrame);
        this._caretFrame = 0;
        this._hideCaret();
    }

    private _measureCaret(): void {
        const selection = document.getSelection();
        const root = this._host.nativeElement.querySelector<HTMLElement>('.ql-editor');
        if (!selection || selection.rangeCount === 0 || !selection.isCollapsed || !root) {
            this._hideCaret();
            return;
        }

        const range = selection.getRangeAt(0);
        if (!root.contains(range.startContainer)) {
            this._hideCaret();
            return;
        }

        let rect: DOMRect | undefined = range.getClientRects()[0];
        if (!rect || (rect.width === 0 && rect.height === 0)) {
            // Linha vazia (ou só o placeholder): o range não tem caixa, a linha tem.
            const node =
                range.startContainer.nodeType === Node.ELEMENT_NODE
                    ? (range.startContainer as Element)
                    : range.startContainer.parentElement;
            const line = node?.closest('p, li, h1, h2, h3, div') ?? node;
            if (!line) return;
            const lineRect = line.getBoundingClientRect();
            rect = new DOMRect(lineRect.left, lineRect.top, 0, lineRect.height);
        }

        const hostRect = this._host.nativeElement.getBoundingClientRect();
        const next = {
            left: rect.left - hostRect.left,
            top: rect.top - hostRect.top,
            height: rect.height,
        };
        // Direto no elemento, no mesmo quadro: passando por signal o estilo só chegava na
        // detecção de mudanças seguinte, e o traço andava um ou dois quadros atrás do texto.
        const el = this._caretEl()?.nativeElement;
        if (el) {
            const at = this._caretAt;
            // `!(… < 0.5)` e não `>= 0.5`: a primeira medida parte de NaN, e qualquer
            // comparação com NaN é falsa (com `>=` a posição nunca era escrita e o traço
            // ficava sem altura, invisível).
            const same = (a: number, b: number): boolean => Math.abs(a - b) < 0.5;
            if (!same(at.left, next.left) || !same(at.top, next.top) || !same(at.height, next.height)) {
                el.style.left = `${next.left}px`;
                el.style.top = `${next.top}px`;
                el.style.height = `${next.height}px`;
                this._caretAt = next;
            }
        }
        if (!this.caretVisible()) this.caretVisible.set(true);
    }

    private _hideCaret(): void {
        if (this.caretVisible()) this.caretVisible.set(false);
    }

    private _applyLocally(value: string): void {
        const field = this.field();
        if (this.target() === 'value') field.value = value;
        else field.description = value;
    }
}
